import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Overlay, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../../AuthContext';
import useApi from '../../Api';
import '../../styles/components/common/ReportButton.css';
import { validateTextInput } from '../common/TextValidator';
import { useNavigate } from 'react-router-dom';
import { ReportReason } from '../../utils/enums';
import { debounce } from 'lodash';
import Popup from './popup/Popup';
import { toast } from 'react-toastify';

const ReportButton = ({ contentId, contentType }) => {
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [additionalCommentsCharCount, setAdditionalCommentsCharCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLoginInfo, setShowLoginInfo] = useState(false);
  const { token, user } = useAuth();
  const apiClient = useApi();
  const navigate = useNavigate();
  const maxCommentLength = 1000;
  const debouncedSubmitRef = useRef();
  const reportButtonRef = useRef();

  useEffect(() => {
    debouncedSubmitRef.current = debounce(async (reportData) => {
      setLoading(true);
      setError('');
      try {
        const response = await apiClient.post(
          '/reports',
          {
            content_id: reportData.contentId,
            content_type: reportData.contentType,
            reason: reportData.reason,
            additional_comments: reportData.additionalComments,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(response.data.message || 'Report submitted successfully.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setShowModal(false);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.detail) {
          const backendError = Array.isArray(err.response.data.detail)
            ? err.response.data.detail.map((error) => error.msg).join(' ')
            : err.response.data.detail;
          setError(backendError);
        } else {
          setError('Failed to submit report. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }, 1000);
    return () => {
      debouncedSubmitRef.current.cancel();
    };
  }, [apiClient, token]);

  useEffect(() => {
    if (submitted) {
      let errorMessages = [];
      if (!reason) {
        errorMessages.push('Please select a reason.');
      }
      if (additionalComments.trim() !== '') {
        const commentsValidation = validateTextInput(additionalComments, {
          maxLength: maxCommentLength,
          allowSpecialChars: true,
        });
        if (!commentsValidation.isValid) {
          errorMessages.push(...commentsValidation.errors.map(err => `Additional Comments: ${err}`));
        }
      }
      setError(errorMessages.join(' '));
    } else {
      setError('');
    }
  }, [reason, additionalComments, maxCommentLength, submitted]);

  const validateInputs = () => {
    let valid = true;
    let errorMessages = [];
    if (!reason) {
      valid = false;
      errorMessages.push('Please select a reason.');
    }
    if (additionalComments.trim() !== '') {
      const commentsValidation = validateTextInput(additionalComments, {
        maxLength: maxCommentLength,
        allowSpecialChars: true,
      });
      if (!commentsValidation.isValid) {
        valid = false;
        errorMessages.push(...commentsValidation.errors.map(err => `Additional Comments: ${err}`));
      }
    }
    if (!valid) {
      setError(errorMessages.join(' '));
    } else {
      setError('');
    }
    return valid;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitted(true);
    if (!validateInputs()) return;
    debouncedSubmitRef.current({
      contentId,
      contentType,
      reason,
      additionalComments,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleReportClick = () => {
    if (!user) {
      setShowLoginInfo(true);
    } else {
      setShowModal(true);
      setSubmitted(false);
    }
  };

  useEffect(() => {
    if (showLoginInfo) {
      const handleClickOutside = (event) => {
        if (reportButtonRef.current && !reportButtonRef.current.contains(event.target)) {
          setShowLoginInfo(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showLoginInfo]);

  useEffect(() => {
    if (!showModal) {
      setReason('');
      setAdditionalComments('');
      setAdditionalCommentsCharCount(0);
      setError('');
      setSubmitted(false);
    }
  }, [showModal]);

  const handleAdditionalCommentsChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxCommentLength) {
      setAdditionalComments(text);
      setAdditionalCommentsCharCount(text.length);
    }
  };

  const reasons = Object.values(ReportReason);
  const reasonOptions = reasons.map((reasonText) => ({
    value: reasonText,
    label: reasonText,
  }));

  const handleReasonChange = (selectedOption) => {
    setReason(selectedOption ? selectedOption.value : '');
  };

  const isSubmitDisabled = loading || !reason || error !== '';

  return (
    <>
      <div ref={reportButtonRef} style={{ display: 'inline-block', position: 'relative' }}>
        <Button variant="outline-danger" onClick={handleReportClick} className="report-button">
          Report
        </Button>
        <Overlay target={reportButtonRef.current} show={showLoginInfo} placement="bottom">
          {(props) => (
            <Tooltip id="login-tooltip" {...props} className="login-tooltip">
              Please login to use
            </Tooltip>
          )}
        </Overlay>
      </div>
      {showModal && (
        <Popup
          title="Report Content"
          onCancel={() => setShowModal(false)}
          className="report-modal"
          footer={
            <button className="report-submit-btn" onClick={handleSubmit} disabled={isSubmitDisabled}>
              {loading ? 'Submitting...' : 'Submit Report'}
            </button>
          }
        >
          <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <Form.Group controlId="formReason">
              <Select
                value={reasonOptions.find((option) => option.value === reason)}
                onChange={handleReasonChange}
                options={reasonOptions}
                placeholder="Select a reason"
                classNamePrefix="react-select"
              />
            </Form.Group>
            <Form.Group controlId="formAdditionalComments">
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Provide additional details (optional)"
                value={additionalComments}
                onChange={handleAdditionalCommentsChange}
                className="form-input"
                maxLength={maxCommentLength}
              />
              <div className="text-muted text-right">
                {additionalCommentsCharCount}/{maxCommentLength} characters
              </div>
            </Form.Group>
          </Form>
          {error && <div className="report-error">{error}</div>}
        </Popup>
      )}
    </>
  );
};

export default ReportButton;
